import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type SelectionControlMeasuresConstantType,
    type SelectionControlColorsConstantType,
    type SelectionControlDynamicStyleType,
    type SelectionControlMeasures,
    type SelectionControlColors,
    type SelectionControlRole
} from "./type";
import type {
    ElevationLevelType
} from "../../helpers/theme/elevation";
import type {
    Mutable
} from "../../types";
import type {
    LabelVariant
} from "../label/type";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHugStyle
} from "../../helpers/theme/spread";
import {
    LABEL_TYPOGRAPHY
} from "../label/stylesheet";
import {
    webStyle
} from "../../utils";

export const SELECTION_CONTROL_NAMES: Record<SelectionControlRole, string> = {
    checkbox: "CheckBox",
    radio: "RadioButton",
    switch: "Switch"
};

export const SELECTION_CONTROL_LABEL_VARIANT: LabelVariant = "body";

export const SELECTION_CONTROL_TEXT_VARIANT: keyof NCoreUIKit.Typography = LABEL_TYPOGRAPHY[SELECTION_CONTROL_LABEL_VARIANT].medium;

export const SELECTION_CONTROL_HINT_VARIANT: keyof NCoreUIKit.Typography = "bodySmallSize";

export const SELECTION_CONTROL_ELEVATION_LEVEL: ElevationLevelType = "level1";

export const SELECTION_CONTROL_TOGGLE_KEY = " ";

export const getSelectionControlMeasures = ({
    controlPaddings,
    controlHeights,
    touchTargets,
    pointerType,
    typography
}: SelectionControlMeasuresConstantType): SelectionControlMeasures => {
    const minHeight = pointerType === "coarse" ? touchTargets.preferred : controlHeights.medium;

    const lineHeight = typography[SELECTION_CONTROL_TEXT_VARIANT].lineHeight;

    return {
        paddingVertical: Math.max(0, (minHeight - lineHeight) / 2),
        gap: controlPaddings.small,
        lineHeight,
        minHeight
    };
};

export const getSelectionControlCardStyle = ({
    isDisabled,
    isChecked,
    radiuses,
    borders,
    isError,
    colors,
    spaces
}: Pick<SelectionControlDynamicStyleType, "isDisabled" | "isChecked" | "radiuses" | "borders" | "colors" | "spaces" | "isError">): ViewStyle => {
    const baseStyle: ViewStyle = {
        paddingHorizontal: spaces.spacingMd,
        paddingVertical: spaces.spacingMd,
        borderRadius: radiuses.actions,
        borderWidth: borders.line,
        alignItems: "flex-start",
        gap: spaces.spacingSm
    };

    if(isDisabled) {
        return {
            ...baseStyle,
            backgroundColor: colors.system.state.overlay.disabled.neutral,
            borderColor: colors.system.state.border.disabled.neutral
        };
    }

    return {
        ...baseStyle,
        borderColor: isError ? colors.content.border.danger : colors.content.border.subtle,
        backgroundColor: isChecked ? colors.content.container.selected : "transparent"
    };
};

export const getSelectionControlColors = ({
    isDisabled,
    isError,
    colors
}: SelectionControlColorsConstantType): SelectionControlColors => {
    if(isDisabled) {
        return {
            hintTextColor: colors.system.state.content.disabled.neutral,
            subTitleColor: colors.content.text.disabled
        };
    }

    return {
        hintTextColor: isError ? colors.content.text.danger : colors.content.text.mid,
        subTitleColor: colors.content.text.mid
    };
};

const stylesheet = StyleSheet.create({
    row: {
        alignItems: "flex-start",
        flexDirection: "row",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    indicatorContainer: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    indicatorOnlyRow: {
        justifyContent: "center"
    },
    container: {
        flexDirection: "column"
    },
    stretchedContainer: {
        alignSelf: "stretch",
        flexShrink: 1,
        width: "100%"
    },
    hintTextContainer: {
        flexDirection: "row",
        alignItems: "center"
    },
    stretchedContent: {
        flexGrow: 1
    },
    hintText: {
        flexShrink: 1
    },
    content: {
        flexShrink: 1
    }
});

export const useStyles = ({
    currentMeasures,
    spreadBehaviour,
    isFocusVisible,
    isTitleVisible,
    shapeConfigs,
    isDisabled,
    isChecked,
    isLoading,
    userStyle,
    radiuses,
    borders,
    isError,
    variant,
    colors,
    spaces
}: SelectionControlDynamicStyleType) => {
    const styles = {
        row: {
            paddingVertical: currentMeasures.paddingVertical,
            minHeight: currentMeasures.minHeight,
            gap: currentMeasures.gap
        } as Mutable<ViewStyle>,
        indicatorContainer: {
            minHeight: currentMeasures.lineHeight
        } as Mutable<ViewStyle>,
        hintTextContainer: {
            marginTop: spaces.spacingXxs,
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        container: {
        } as Mutable<ViewStyle>,
        content: {
        } as Mutable<ViewStyle>
    };

    if(!isTitleVisible) {
        Object.assign(styles.row, stylesheet.indicatorOnlyRow, {
            minWidth: currentMeasures.minHeight
        });
    }

    if(variant === "card") {
        Object.assign(styles.row, getSelectionControlCardStyle({
            isDisabled,
            isChecked,
            radiuses,
            borders,
            isError,
            colors,
            spaces
        }), getCornerStyle({
            radius: radiuses.actions,
            shapeConfigs
        }), getFocusRingStyle({
            isFocusVisible,
            borders,
            colors
        }));
    }

    if(isLoading) {
        Object.assign(styles.row, webStyle({
            cursor: "not-allowed"
        }));
    }

    if(spreadBehaviour === "baseline") {
        Object.assign(styles.container, getHugStyle({
            style: userStyle
        }));
    }

    if(spreadBehaviour === "stretch") {
        Object.assign(styles.container, stylesheet.stretchedContainer);

        Object.assign(styles.content, stylesheet.stretchedContent);
    }

    return styles;
};
export default stylesheet;
